import { AU_IN_METRES } from './reference-data';

/**
 * Canonical editable safe ranges (a `core` leaf, AD-1: pure, no I/O). This is the
 * SINGLE source of the editable clamps so the Story 5.1 share codec's clamps are
 * PROVABLY the reducer's clamps (AD-2/AD-11 anti-drift): there is now exactly one
 * declaration of each range, imported by every consumer.
 *
 * Layering (AD-2 layer table): `core` is a leaf; `state`/`share` may import `core`
 * directly, so `state/reducer.ts` and `share/codec.ts` import these here. `ui`
 * (format.ts, PlaybackHud.svelte) must NOT import `core` directly, so it consumes
 * these THROUGH the `state/selectors.ts` re-export, exactly as it already sources
 * `AU_IN_METRES`. The value is identical either way; the state hop only preserves
 * the layer rule and does not weaken "one source".
 *
 * Values preserved verbatim from the pre-existing scattered literals:
 *   - e in [0, 0.95]                (was reducer.ts private MIN/MAX_ECCENTRICITY)
 *   - a in [0.01, 100] AU           (was ui/format.ts MIN/MAX_SEMI_MAJOR_AXIS_AU)
 *   - speed in [0.1, 365] days/sec  (was reducer.ts private MIN/MAX_SPEED_*)
 * The sector-count range stays canonical in `core/sectors.ts` (MIN/MAX_SECTOR_COUNT);
 * it is NOT re-declared here.
 */

/** Editable eccentricity range (FR-03); the core type permits e in [0, 1). */
export const ECCENTRICITY_MIN = 0;
export const ECCENTRICITY_MAX = 0.95;

/** Editable semi-major-axis range in AU (FR-04). */
export const SEMI_MAJOR_AXIS_MIN_AU = 0.01;
export const SEMI_MAJOR_AXIS_MAX_AU = 100;

/**
 * The same semi-major-axis range in SI metres, derived ONCE via `AU_IN_METRES`
 * (AD-5: the single conversion factor). The share codec clamps in SI metres (it
 * stores `a` verbatim in metres for exact round-trip), so it uses these.
 */
export const SEMI_MAJOR_AXIS_MIN_METRES = SEMI_MAJOR_AXIS_MIN_AU * AU_IN_METRES;
export const SEMI_MAJOR_AXIS_MAX_METRES = SEMI_MAJOR_AXIS_MAX_AU * AU_IN_METRES;

/** Playback-speed range in simulated-days per real-second (FR-10, Story 1.5). */
export const SPEED_MIN_DAYS_PER_SECOND = 0.1;
export const SPEED_MAX_DAYS_PER_SECOND = 365;
